/**
 * 输入抽象：把三端的设备差异压成一个 `InputSnapshot`（架构 §4.1）。
 *
 * 原则：
 *  - 只有「轴」和「边沿触发」两种语义，不出现任何设备名；
 *  - 走路相对**相机朝向**，不相对世界（否则转个身走路的键位会变）；
 *  - 端点判定放在 `Player` 里，这里只负责采样。
 */

export interface InputSnapshot {
  /** 前后：+1 朝前（相机看向的方向） */
  forward: number;
  /** 左右：+1 朝右 */
  strafe: number;
  /** 上帧到本帧之间按下了「坐/站」 */
  sit: boolean;
}

const KEY_MAP: Record<string, [number, number]> = {
  KeyW: [1, 0],
  ArrowUp: [1, 0],
  KeyS: [-1, 0],
  ArrowDown: [-1, 0],
  KeyA: [0, -1],
  ArrowLeft: [0, -1],
  KeyD: [0, 1],
  ArrowRight: [0, 1],
};

export class InputHub {
  private keys = new Set<string>();

  /** 触摸摇杆的当前值（-1..1） */
  private touchForward = 0;
  private touchStrafe = 0;

  /** 边沿触发队列：本帧内被按下过就为 true，sample 后清空 */
  private pendingSit = false;

  constructor(private readonly target: HTMLElement = window.document.body) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    // 失焦时清空按键，否则 ALT+TAB 回来会一直往前走
    window.addEventListener('blur', this.clear);
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.clear);
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.repeat) return;
    if (e.code === 'KeyC') this.pendingSit = true;
    if (KEY_MAP[e.code] || e.code === 'KeyC') {
      // 避免 WASD/空格把页面滚走
      if (this.target && e.code !== 'F5' && e.code !== 'F12') e.preventDefault?.();
    }
    this.keys.add(e.code);
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code);
  };

  private clear = (): void => {
    this.keys.clear();
    this.touchForward = 0;
    this.touchStrafe = 0;
  };

  /** 供触摸摇杆 / 手柄适配器调用 */
  setMoveAxis(forward: number, strafe: number): void {
    this.touchForward = clamp(forward);
    this.touchStrafe = clamp(strafe);
  }

  /** 供触摸按钮 / VR 手柄调用 */
  pressSit(): void {
    this.pendingSit = true;
  }

  sample(): InputSnapshot {
    let forward = this.touchForward;
    let strafe = this.touchStrafe;

    for (const code of this.keys) {
      const axis = KEY_MAP[code];
      if (axis) {
        forward += axis[0];
        strafe += axis[1];
      }
    }

    const snapshot: InputSnapshot = {
      forward: clamp(forward),
      strafe: clamp(strafe),
      sit: this.pendingSit,
    };
    this.pendingSit = false;
    return snapshot;
  }
}

function clamp(v: number): number {
  return v > 1 ? 1 : v < -1 ? -1 : v;
}
